import {
  allTemplateTokens,
  findTokensOutsideContext,
  findUnknownTokens,
  MAX_MESSAGE_TEMPLATE_LENGTH,
  type TemplateContext,
  type TemplateTokenContext,
  templateTokensForContext,
} from "@unbox/api";
import { Eye, EyeOff, Image03, InfoCircle } from "@untitledui/icons";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useStoreComposerImage } from "@/api/message-images";
import {
  useCreateMessageTemplate,
  useMessageTemplateCategories,
  useMessageTemplates,
  useUpdateMessageTemplate,
} from "@/api/message-templates";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { error as toastError } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import {
  ACCEPTED_COMPOSER_IMAGE_TYPES,
  type ComposerImage,
  composerImageFromFile,
  composerImageFromStored,
  IMAGE_UPLOAD_HINT,
  releaseComposerImage,
} from "./composer-image";
import { ComposerPreview, SAMPLE_PREVIEW_CONTEXT, usePreviewPresence } from "./composer-preview";
import {
  toastTemplateNotSaved,
  toastTemplateNotUpdated,
  toastTemplateSaved,
  toastTemplateTooLong,
  toastTemplateUpdated,
} from "./composer-toasts";
import {
  ComposerPortalContainer,
  EmojiMenu,
  TemplateActions,
  TemplatesMenu,
  toolbarButtonClassName,
  VariableMenu,
} from "./composer-toolbar";
import { HighlightedTextarea } from "./highlighted-textarea";
import { ImageAttachment } from "./image-attachment";
import { SaveAsTemplateDialog } from "./save-as-template-dialog";

const MESSAGE_COMPOSER_PLACEHOLDER =
  "Type your message, or use the tools below to add templates, variables and images…";

type MenuId = "variable" | "template" | "emoji" | "template-actions";

export type ComposerPrimaryAction = {
  label: ReactNode;
  onClick: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

export type MessageComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  variableContext: TemplateTokenContext | "all";
  image?: ComposerImage | null;
  onImageChange?: (image: ComposerImage | null) => void;
  previewContext?: TemplateContext;
  unavailableVariables?: Record<string, string>;
  templates?: boolean;
  emoji?: boolean;
  initialTemplateId?: string | null;
  onTemplateChange?: (templateId: string | null) => void;
  primaryAction?: ComposerPrimaryAction;
  submitOnEnter?: boolean;
  showActions?: boolean;
  preview?: boolean;
  onPreviewChange?: (open: boolean) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  minHeight?: number;
  textClassName?: string;
  imageRejectedTitle?: string;
  children?: ReactNode;
  className?: string;
  "aria-label"?: string;
};

export function MessageComposer({
  value,
  onChange,
  onBlur,
  variableContext,
  image = null,
  onImageChange,
  previewContext = SAMPLE_PREVIEW_CONTEXT,
  unavailableVariables,
  templates: templatesEnabled = true,
  emoji = false,
  initialTemplateId = null,
  onTemplateChange,
  primaryAction,
  submitOnEnter = false,
  showActions = true,
  preview: controlledPreview,
  onPreviewChange,
  placeholder = MESSAGE_COMPOSER_PLACEHOLDER,
  autoFocus,
  disabled,
  invalid,
  maxLength = MAX_MESSAGE_TEMPLATE_LENGTH,
  minHeight = 110,
  textClassName,
  imageRejectedTitle = "Image not attached",
  children,
  className,
  "aria-label": ariaLabel = "Message",
}: MessageComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [internalPreview, setInternalPreview] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(initialTemplateId);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const rootRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node?.closest<HTMLElement>('[role="dialog"][data-rac]') ?? null);
  }, []);

  const { data: templates = [] } = useMessageTemplates();
  const { mutateAsync: createTemplate, isPending: isCreatingTemplate } = useCreateMessageTemplate();
  const { mutateAsync: updateTemplate } = useUpdateMessageTemplate();
  const { store: storeImage, isPending: isStoringImage } = useStoreComposerImage();

  const hasText = value.trim().length > 0;
  const previewOpen = (controlledPreview ?? internalPreview) && hasText;
  const { mounted: previewMounted, closing: previewClosing } = usePreviewPresence(previewOpen);
  const tokens = useMemo(
    () => (variableContext === "all" ? allTemplateTokens() : templateTokensForContext(variableContext)),
    [variableContext],
  );
  const flaggedTokens = useMemo(
    () => (variableContext === "all" ? findUnknownTokens(value) : findTokensOutsideContext(value, variableContext)),
    [value, variableContext],
  );
  const loadedTemplate = templates.find((t) => t.id === loadedTemplateId) ?? null;
  const categories = useMessageTemplateCategories();

  const setPreview = useCallback(
    (open: boolean) => {
      setInternalPreview(open);
      onPreviewChange?.(open);
    },
    [onPreviewChange],
  );

  const loadTemplate = (templateId: string | null) => {
    setLoadedTemplateId(templateId);
    onTemplateChange?.(templateId);
  };

  const rememberSelection = () => {
    const textarea = textareaRef.current;
    if (textarea) selectionRef.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
  };

  const insertAtCursor = (text: string) => {
    const selection = selectionRef.current ?? { start: value.length, end: value.length };
    const next = value.slice(0, selection.start) + text + value.slice(selection.end);
    if (next.length > maxLength) return;
    const caret = selection.start + text.length;
    selectionRef.current = { start: caret, end: caret };
    setPreview(false);
    onChange(next);
  };

  const refocusTextarea = (event: Event) => {
    event.preventDefault();
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      const selection = selectionRef.current;
      if (!textarea) return;
      textarea.focus();
      if (selection) textarea.setSelectionRange(selection.start, selection.end);
    });
  };

  const pickTemplate = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    if (template.content.length > maxLength) {
      toastTemplateTooLong(maxLength);
      return;
    }
    setPreview(false);
    onChange(template.content);
    selectionRef.current = null;
    const templateImage = composerImageFromStored(template.imageId, template.imageUrl);
    if (onImageChange && templateImage) {
      releaseComposerImage(image);
      onImageChange(templateImage);
    }
    loadTemplate(template.id);
  };

  const pickImage = (file: File | undefined) => {
    if (!file || !onImageChange) return;
    const result = composerImageFromFile(file);
    if ("error" in result) {
      toastError(imageRejectedTitle, { description: result.error });
      return;
    }
    releaseComposerImage(image);
    setPreview(false);
    onImageChange(result.image);
  };

  const removeImage = () => {
    releaseComposerImage(image);
    onImageChange?.(null);
  };

  const storeImageForTemplate = async () => {
    const imageId = await storeImage(image);
    if (image && imageId && !image.imageId) onImageChange?.({ ...image, imageId });
    return imageId;
  };

  const saveAsNewTemplate = async ({ name, category }: { name: string; category: string }) => {
    try {
      const imageId = onImageChange ? await storeImageForTemplate() : null;
      const created = await createTemplate({ name, category, content: value.trim(), imageId });
      loadTemplate(created.id);
      toastTemplateSaved(name, category);
      return true;
    } catch {
      toastTemplateNotSaved();
      return false;
    }
  };

  const updateLoadedTemplate = async () => {
    if (!loadedTemplate) return;
    try {
      const imageId = onImageChange ? await storeImageForTemplate() : undefined;
      await updateTemplate({ id: loadedTemplate.id, input: { content: value.trim(), imageId } });
      toastTemplateUpdated(loadedTemplate.name);
    } catch {
      toastTemplateNotUpdated();
    }
  };

  const primaryActionDisabled = primaryAction?.isDisabled ?? (!hasText || !!disabled);

  const templateIsDirty =
    !!loadedTemplate &&
    (value.trim() !== loadedTemplate.content.trim() ||
      (!!onImageChange && (image?.imageId ?? null) !== (loadedTemplate.imageId ?? null)));

  const menuState = (id: MenuId) => ({
    open: openMenu === id,
    onOpenChange: (open: boolean) => setOpenMenu(open ? id : null),
  });

  const previewButton = (
    <ComposerPreviewButton open={previewOpen} isDisabled={!hasText} onToggle={() => setPreview(!previewOpen)} />
  );

  return (
    <ComposerPortalContainer.Provider value={portalContainer}>
      <div ref={rootRef} className={cx("flex flex-col gap-3", className)}>
        <div
          className={cx(
            "flex flex-col rounded-xl border bg-primary transition-shadow duration-100 focus-within:ring-4",
            invalid
              ? "border-error focus-within:border-error focus-within:ring-error-100"
              : "border-gray-300 focus-within:border-brand-300 focus-within:ring-brand-100",
          )}
        >
          {previewMounted ? (
            <ComposerPreview
              message={value}
              image={image}
              context={previewContext}
              closing={previewClosing}
              minHeight={minHeight}
            />
          ) : (
            <>
              <HighlightedTextarea
                ref={textareaRef}
                value={value}
                flaggedTokens={flaggedTokens}
                onChange={(next) => {
                  onChange(next);
                  rememberSelection();
                }}
                onBlur={() => {
                  rememberSelection();
                  onBlur?.();
                }}
                onKeyDown={(e) => {
                  if (!submitOnEnter || !primaryAction || e.key !== "Enter" || e.shiftKey) return;
                  e.preventDefault();
                  if (!primaryActionDisabled && !primaryAction.isLoading) primaryAction.onClick();
                }}
                placeholder={placeholder}
                autoFocus={autoFocus}
                disabled={disabled}
                maxLength={maxLength}
                minHeight={minHeight}
                textClassName={textClassName}
                aria-label={ariaLabel}
                aria-invalid={invalid}
              />
              {image && (
                <div className="px-3.5 pb-3">
                  <ImageAttachment
                    image={image}
                    disabled={disabled}
                    onReplace={() => fileInputRef.current?.click()}
                    onRemove={removeImage}
                  />
                </div>
              )}
            </>
          )}
          {onImageChange && (
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_COMPOSER_IMAGE_TYPES.join(",")}
              hidden
              onChange={(e) => {
                pickImage(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          )}
          {!previewMounted && (
            <div className="relative flex flex-wrap items-center gap-2 border-t border-secondary px-3 py-2.5">
              {onImageChange && (
                <Tooltip title={IMAGE_UPLOAD_HINT} placement="top">
                  <TooltipTrigger
                    isDisabled={disabled}
                    onPress={() => fileInputRef.current?.click()}
                    aria-pressed={!!image}
                    className={cx(
                      toolbarButtonClassName,
                      "px-3.5",
                      image ? "border-brand-500" : "border-gray-300 hover:bg-primary_hover",
                    )}
                  >
                    <Image03 aria-hidden className="size-4" />
                    Image
                  </TooltipTrigger>
                </Tooltip>
              )}
              <VariableMenu
                {...menuState("variable")}
                tokens={tokens}
                unavailable={unavailableVariables}
                disabled={disabled}
                onInsert={insertAtCursor}
                onCloseAutoFocus={refocusTextarea}
              />
              {templatesEnabled && (
                <TemplatesMenu
                  {...menuState("template")}
                  templates={templates}
                  loadedTemplateId={loadedTemplateId}
                  disabled={disabled}
                  onPick={pickTemplate}
                />
              )}
              {emoji && (
                <EmojiMenu
                  {...menuState("emoji")}
                  disabled={disabled}
                  onInsert={insertAtCursor}
                  onCloseAutoFocus={refocusTextarea}
                />
              )}
              {templatesEnabled && (
                <div className="ml-auto">
                  <TemplateActions
                    loadedTemplateName={loadedTemplate?.name ?? null}
                    canSave={hasText && !disabled}
                    canUpdate={templateIsDirty && !isStoringImage}
                    menuOpen={openMenu === "template-actions"}
                    onMenuOpenChange={(open) => setOpenMenu(open ? "template-actions" : null)}
                    onSaveAsNew={() => setSaveDialogOpen(true)}
                    onUpdate={updateLoadedTemplate}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {!previewMounted && flaggedTokens.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-utility-warning-200 bg-utility-warning-50 px-3 py-2 text-xs text-utility-warning-700">
            <InfoCircle className="mt-px size-4 shrink-0" />
            <span>
              Variable{flaggedTokens.length > 1 ? "s" : ""} not available here: {flaggedTokens.join(", ")}. They won't
              be filled in.
            </span>
          </div>
        )}

        {children}

        {showActions && (
          <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
            {previewButton}
            {primaryAction && (
              <Button
                size="sm"
                isDisabled={primaryActionDisabled}
                isLoading={primaryAction.isLoading}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </Button>
            )}
          </div>
        )}

        {templatesEnabled && (
          <SaveAsTemplateDialog
            open={saveDialogOpen}
            onOpenChange={setSaveDialogOpen}
            categories={categories}
            isSaving={isCreatingTemplate || isStoringImage}
            onSave={saveAsNewTemplate}
            portalContainer={portalContainer}
          />
        )}
      </div>
    </ComposerPortalContainer.Provider>
  );
}

export function ComposerPreviewButton({
  open,
  isDisabled,
  onToggle,
  className,
}: {
  open: boolean;
  isDisabled?: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <Button
      color="secondary"
      size="sm"
      iconLeading={open ? EyeOff : Eye}
      isDisabled={isDisabled}
      onClick={onToggle}
      className={cx(
        "bg-brand-secondary text-brand-secondary ring-0 *:data-icon:text-current hover:bg-brand-secondary_hover hover:text-brand-secondary_hover hover:*:data-icon:text-current",
        className,
      )}
    >
      {open ? "Close preview" : "Preview"}
    </Button>
  );
}
